(function () {
  'use strict';

  /* ============================================================
     Aurum Brew — Roast Lot page
     assets/aurum-lot.js

     Two jobs:

       1. Lift the server-rendered body out of its hidden host and into the
          page, the same arrangement the policy pages use. Custom sections do
          not reach the live theme from GitHub on this store, so the markup is
          rendered by a snippet into a hidden div and moved here.

       2. Complete the freshness sentence. The age has to be computed in the
          browser: a server-rendered "11 days ago" is wrong the moment the
          page is cached, and this is the one line on the page a customer is
          most likely to read while holding the bag.

     The snippet already prints a complete, correct sentence as a fallback
     ("Roasted 3 March 2026."), so if this file never loads the page still
     reads properly. Nothing here is required for the page to make sense.
     ============================================================ */

  function mount() {
    var host = document.getElementById('alot-host');
    if (!host || host.dataset.alotMounted) return;

    var main = document.getElementById('MainContent') ||
               document.querySelector('main') ||
               document.body;
    if (!main) return;

    var shell = document.createElement('div');
    shell.className = 'alot';
    while (host.firstChild) shell.appendChild(host.firstChild);

    /* Replace the theme's own page body rather than appending after it: the
       metaobject template renders through main-page, which outputs an empty
       article that would otherwise leave a gap above the hero. */
    var pageBody = main.querySelector('.page-width, .shopify-policy__container');
    if (pageBody && pageBody.parentNode) {
      pageBody.parentNode.replaceChild(shell, pageBody);
    } else {
      main.insertBefore(shell, main.firstChild);
    }

    /* Only now, once the body is actually in the page. The class carries the
       cream background and the palette; setting it earlier would recolour a
       page whose content never arrived. */
    document.documentElement.classList.add('alot-page');

    host.dataset.alotMounted = '1';
    freshness(host, shell);
    instruments(shell);
  }

  /* ------------------------------------------------------------------
     The stat instruments.

     Everything visual lives in CSS; this only decides WHEN. The class it
     adds turns the animation on, and the drawings are already correct
     without it, so a failure here costs the motion and nothing else.
     ------------------------------------------------------------------ */
  function instruments(shell) {
    var strip = shell.querySelector('.alot-stats');
    if (!strip) return;

    function play() {
      /* Removing and forcing a reflow before re-adding is what makes a
         replay actually restart. Setting the same class again is a no-op to
         the engine, and the animation would never run a second time. */
      strip.classList.remove('alot-anim');
      void strip.offsetWidth;
      strip.classList.add('alot-anim');
    }

    /* Fire when the strip is actually looked at rather than on load: it sits
       below the fold on a phone, and an animation that has already finished
       by the time it is scrolled to may as well not exist. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { play(); io.disconnect(); }
        });
      }, { threshold: 0.4 });
      io.observe(strip);
    } else {
      play();
    }

    /* Replay on hover, and on keyboard focus so it is not mouse-only. */
    strip.addEventListener('mouseenter', play);
    strip.setAttribute('tabindex', '0');
    strip.addEventListener('focus', play);
  }

  function freshness(host, shell) {
    var iso = host.getAttribute('data-roasted');
    if (!iso) return;                       /* no date: leave the fallback alone */

    var line = shell.querySelector('[data-fresh-line]');
    if (!line) return;

    /* Parse as local midnight. new Date('2026-03-03') is parsed as UTC, which
       reads as "yesterday" for anyone west of Greenwich for most of the day. */
    var parts = iso.split('-');
    if (parts.length !== 3) return;
    var roasted = new Date(+parts[0], +parts[1] - 1, +parts[2]);
    if (isNaN(roasted.getTime())) return;

    var today = new Date();
    today = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    var days = Math.round((today - roasted) / 86400000);
    if (days < 0) return;                   /* dated in the future: say nothing */

    var age;
    if (days === 0) age = 'today';
    else if (days === 1) age = 'yesterday';
    else if (days < 14) age = days + ' days ago';
    else if (days < 60) {
      var w = Math.round(days / 7);
      age = w + ' week' + (w === 1 ? '' : 's') + ' ago';
    } else {
      var mo = Math.round(days / 30);
      age = mo + ' month' + (mo === 1 ? '' : 's') + ' ago';
    }

    /* data-place comes from the snippet and wins whenever it is present, so
       the two sentences can never name different countries for the same lot.

       The literal below is the fallback for when it is absent. That is not
       hypothetical on this store: assets/ syncs from GitHub within a minute
       or two, snippets/ sometimes does not arrive for far longer, so the
       page can and did run this script against an older snippet. Roasting at
       origin is the same for every lot, so a default here is safe, and it
       means the line reads correctly through the gap rather than silently
       dropping the country.

       If a second roastery ever opens, delete the default: data-place is the
       real source and this literal must not be allowed to outlive it. */
    var place = host.getAttribute('data-place') || 'Costa Rica';

    /* Built as nodes rather than an innerHTML string. getAttribute returns the
       DECODED attribute value, so Liquid's escape filter does not protect an
       innerHTML assignment downstream of it — text that arrived as an entity
       would be re-parsed as markup. Nothing here is user-supplied today, but
       this is a page that renders shop data and the safe form costs nothing. */
    var strong = document.createElement('strong');
    strong.textContent = age;

    line.textContent = place ? 'Roasted in ' + place + ' ' : 'Roasted ';
    line.appendChild(strong);
    line.appendChild(document.createTextNode('.'));

    /* The advisory underneath is where the honesty lives. Coffee does not
       spoil, it fades, so this says "at its best" rather than inventing an
       expiry date the roastery has not tested for. */
    var sub = shell.querySelector('.alot-fresh__sub');
    if (sub) {
      var best = new Date(roasted.getTime());
      best.setDate(best.getDate() + 45);
      var months = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
      var bestStr = best.getDate() + ' ' + months[best.getMonth()];
      if (days <= 45) {
        sub.textContent = 'At its best through ' + bestStr + '.';
      } else {
        sub.textContent = 'Past its brightest, but still good coffee. Best within about six weeks of roasting.';
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
